import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../common/components/functional/dialogs/BasicDialog";
import CustomerUsersAutocomplete from "../../common/components/functional/fields/customerUsersAutocomplete/CustomerUsersAutocomplete";
import { CustomerUserAutocompleteOption } from "../../common/components/functional/fields/customerUsersAutocomplete/customerUsersAutocompleteUtils";
import TalentAutocomplete from "../../common/components/functional/fields/talentAutocomplete/TalentAutocomplete";
import { TalentAutocompleteOption } from "../../common/components/functional/fields/talentAutocomplete/talentAutocompleteUtils";
import { SelectAutocompleteSingle } from "../../common/components/functional/SelectAutocomplete/SelectAutocompleteSingle";
import { AutocompleteType } from "../../common/types/autoComplete";
import { Nullable } from "../../common/types/nullable";
import { TalentStatus } from "../talents/types/common";
import {
  toCustomerParticipant,
  toTalentParticipant,
} from "./addParticipantMappers";
import { ParticipantOption, ParticipantType } from "./types/common";

const RECIPIENT_TYPE_OPTIONS: AutocompleteType[] = [
  { id: ParticipantType.CUSTOMER, name: "CUSTOMER" },
  { id: ParticipantType.TALENT, name: "TALENT" },
];

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (participant: ParticipantOption) => void;
  allowedRecipientTypes: ParticipantType[];
};

const AddParticipantDialog = ({
  open,
  onClose,
  onConfirm,
  allowedRecipientTypes,
}: Props) => {
  const { t } = useTranslation("messaging");
  const [recipientType, setRecipientType] =
    useState<Nullable<AutocompleteType>>(null);
  const [talent, setTalent] =
    useState<Nullable<TalentAutocompleteOption>>(null);
  const [customerUser, setCustomerUser] =
    useState<Nullable<CustomerUserAutocompleteOption>>(null);

  const recipientTypeOptions = RECIPIENT_TYPE_OPTIONS.filter((option) =>
    allowedRecipientTypes.includes(option.id as ParticipantType),
  );
  const showRecipientTypeSelect = recipientTypeOptions.length > 1;
  const impliedRecipientType =
    recipientTypeOptions.length === 1 ? recipientTypeOptions[0] : null;
  const resolvedRecipientType = recipientType ?? impliedRecipientType;
  const selectedType = resolvedRecipientType?.id;
  const recipientSelected =
    selectedType === ParticipantType.TALENT ? talent : customerUser;

  useEffect(() => {
    if (open) return;
    setRecipientType(null);
    setTalent(null);
    setCustomerUser(null);
  }, [open]);

  const handleTypeChange = (value: Nullable<AutocompleteType>) => {
    setRecipientType(value);
    setTalent(null);
    setCustomerUser(null);
  };

  const handleConfirm = () => {
    if (selectedType === ParticipantType.TALENT && talent) {
      onConfirm(toTalentParticipant(talent));
      return;
    }

    if (selectedType === ParticipantType.CUSTOMER && customerUser) {
      onConfirm(toCustomerParticipant(customerUser));
    }
  };

  return (
    <BasicDialog
      open={open}
      title={t("addParticipantDialogTitle")}
      onCancel={onClose}
      onConfirm={handleConfirm}
      confirmDisabled={!selectedType || !recipientSelected}
      confirmLabel={t("addRecipient")}
      variant="white"
      PaperProps={{ sx: { minWidth: "480px", maxWidth: "560px" } }}
      content={
        <Stack spacing={2}>
          {showRecipientTypeSelect && (
            <SelectAutocompleteSingle
              value={recipientType}
              options={recipientTypeOptions}
              onChange={handleTypeChange}
              placeholder={t("selectRecipientType")}
            />
          )}
          {selectedType === ParticipantType.CUSTOMER ? (
            <CustomerUsersAutocomplete
              value={customerUser}
              onChange={setCustomerUser}
              placeholder={t("selectRecipient")}
            />
          ) : (
            <TalentAutocomplete
              value={talent}
              onChange={setTalent}
              disabled={selectedType !== ParticipantType.TALENT}
              placeholder={t("selectRecipient")}
              status={TalentStatus.ACTIVE}
              helperText={
                selectedType === ParticipantType.TALENT
                  ? t("talentReadyForWorkHelper")
                  : undefined
              }
            />
          )}
        </Stack>
      }
    />
  );
};

export default AddParticipantDialog;
